import { useEffect, useState } from 'react';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';
import type { Country, FlagProps } from 'react-phone-number-input';
import countryLabels from 'react-phone-number-input/locale/en.json';
import flags from 'react-phone-number-input/flags';

import {
  Box,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip
} from '@chakra-ui/react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faAngleDown, faGlobe } from '@fortawesome/pro-regular-svg-icons';

export type CountryCodeSelection = {
  country: Country;
  countryName: string;
  countryCode: string;
};

type SelectCountryCodeProps = {
  value: string | undefined;
  defaultValue?: CountryCodeSelection;
  placeholder?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  onChange: (value?: CountryCodeSelection) => void;
};

export const getCountryLabelsWithCallingCodes = () => {
  return getCountries()
    .map(country => {
      const countryName = countryLabels[country] || country;
      return {
        country,
        countryName,
        countryCode: `+${getCountryCallingCode(country)}`
      };
    })
    .sort((left, right) => left.countryName.localeCompare(right.countryName));
};

export const getCountryData = (countryCode?: string) => {
  const detectedCountry = countryCode?.toUpperCase() as Country | undefined;

  if (!detectedCountry || !getCountries().includes(detectedCountry)) return;
  const countryCallingCode = `+${getCountryCallingCode(detectedCountry)}`;
  const countryName =
    countryLabels[detectedCountry as keyof typeof countryLabels] ||
    detectedCountry;

  return {
    country: detectedCountry,
    countryCode: countryCallingCode,
    countryName
  };
};

const SelectCountryCode = (props: SelectCountryCodeProps) => {
  const { value, defaultValue, onChange } = props;

  const resolveCountry = (countryValue?: string) => {
    if (!countryValue) return defaultValue;
    return getCountryData(countryValue) ?? defaultValue;
  };

  const [country, setCountry] = useState<CountryCodeSelection | undefined>(
    resolveCountry(value)
  );

  const [open, setOpen] = useState(false);

  const onToggle = () => setOpen(prev => !prev);
  const onClose = () => setOpen(false);

  const options = getCountryLabelsWithCallingCodes();

  useEffect(() => {
    setCountry(resolveCountry(value));
  }, [value, defaultValue]);

  const handleChange = (country: CountryCodeSelection | undefined) => {
    setCountry(country);
    onChange(country);
    onClose();
  };

  return (
    <Popover
      placement="bottom-start"
      trigger="click"
      isOpen={open}
      onOpen={onToggle}
      onClose={onClose}
      closeOnBlur
    >
      <PopoverTrigger>
        <Flex
          as="button"
          type="button"
          aria-label={
            country
              ? `Selected country ${country.countryName}`
              : 'Select country code'
          }
          aria-haspopup="dialog"
          p={2}
          borderRight={1}
          borderColor={'black'}
          borderStyle="solid"
          cursor={'pointer'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <Box h={'6'} w={'7'}>
            {country ? (
              <FlagComponent
                country={country.country}
                countryName={country.countryName}
              />
            ) : (
              <Tooltip label="Select country" placement="top" hasArrow>
                <FontAwesomeIcon size="lg" icon={faGlobe} />
              </Tooltip>
            )}
          </Box>
          <FontAwesomeIcon size="xs" icon={faAngleDown} />
        </Flex>
      </PopoverTrigger>

      <PopoverContent
        w="280px"
        p={2}
        rounded={'sm'}
        border="1px solid"
        boxShadow="md"
      >
        <PopoverBody p={0}>
          <Box maxH={'300'} overflowY="auto">
            {options.map(option => (
              <Flex
                key={option.country}
                as="button"
                type="button"
                width="100%"
                align="center"
                gap={3}
                px={3}
                py={2}
                textAlign="left"
                _hover={{ bg: 'gray.50' }}
                onClick={() => handleChange(option)}
              >
                <Text flex="1">{option.countryName}</Text>
              </Flex>
            ))}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

const FlagComponent = ({ country, countryName }: FlagProps) => {
  const Flag = flags[country as keyof typeof flags];

  return (
    <Tooltip label={countryName} placement="top" hasArrow>
      <Flex
        border={'1px'}
        borderColor="neutralPrimaryBorder"
        w="full"
        h="full"
        role="img"
        aria-label={countryName}
      >
        {Flag ? <Flag title={countryName} /> : null}
      </Flex>
    </Tooltip>
  );
};

export default SelectCountryCode;
